import { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import PortfolioOverviewPanel from '../features/portfolio-overview-panel/PortfolioOverviewPanel'
import NoSite from '../components/NoSite'
import { Grid, CircularProgress } from '@mui/material'
import { makeStyles } from '@mui/styles'
import PortfolioOverviewTable from '../features/portfolio-overview-table/PortfolioOverviewTable'
import InfoBox from '../common/dashboard/InfoBox'
import { setSelectedUserSetup } from '../redux/actions/userSetups'
import Api from '../api'
import { loadStateFromLocalStorage } from '../redux/utilsLocalStorage'
import GlobalLeafletMap from '../maps/PortfolioOverview/GlobalLeafletMap'
import { getColorGreenWhiteRed } from '../features/quality-monitoring/QualityMonitoringSetups'
import L from 'leaflet'

const useStyles = makeStyles(theme => ({
  title: {
    marginLeft: theme.spacing(4),
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mainGrid: {
    width: '100%',
    height: '100%',
    padding: theme.spacing(6),
    backgroundColor: theme.palette.background.default,
    display: 'flex',
    justifyContent: 'start',
    alignItems: 'start',
  },
  tableGrid: {
    height: '40%',
    display: 'flex',
  },
  mapGrid: {
    height: '55%',
    display: 'flex',
  },
  dataGrid: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'start',
    alignItems: 'start',
  },
  icons: {
    transform: 'scale(0.5)',
  },
  centerItem: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%',
  },
  alignNoData: {
    marginRight: '50px',
  },
}))

const getDahErrorRange = data => {
  if (!Array.isArray(data) || data.length === 0) {
    return { min: null, max: null }
  }

  let min = Infinity
  let max = -Infinity

  for (const item of data) {
    const value = parseFloat(item.DahError)
    if (!isNaN(value)) {
      if (value < min) min = value
      if (value > max) max = value
    }
  }

  return {
    minDahError: min === Infinity ? null : min,
    maxDahError: max === -Infinity ? null : max,
  }
}

const getNormalizedUnit = unit => {
  const unitMapping = {
    kW: 'kW',
    kWh: 'kW',
    MW: 'MW',
    MWh: 'MW',
  }
  return unitMapping[unit] || 'kW'
}

const techColors = {
  Wind: '#F05039',
  Solar: '#1F449C',
  Default: '#CCCCCC',
}

const minSize = 6
const maxSize = 30

const getRadius = (installedCapacity, min, max) => {
  if (max === min) return 12 // or some default size
  return (
    ((installedCapacity - min) / (max - min)) * (maxSize - minSize) + minSize
  )
}

const PortfolioOverviewPage = props => {
  const {
    container,
    selectedSetup,
    userSetups,
    setSelectedUserSetup,
    userPermissions,
  } = props
  const classes = useStyles(props)
  const [tableData, setTableData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [pageError, setPageError] = useState(false)
  const [selectedPlants, setSelectedPlants] = useState([])
  const [filteredPlants, setFilteredPlants] = useState(null) // null = no filters, [] = filters with no results
  const [mapData, setMapData] = useState([])
  const [nodataContent, setNodataContent] = useState({})
  const [isOverviewDAH, setIsOverviewDAH] = useState(null)

  const storedUnit = loadStateFromLocalStorage('unit') || 'kW'
  const selectedUnit = getNormalizedUnit(storedUnit)

  // turbine data
  const [turbineDataRaw, setTurbineDataRaw] = useState(null) // API response
  const [turbineLoading, setTurbineLoading] = useState(true)
  const [turbineData, setTurbineData] = useState([]) // filtered for selectedPlants
  const [latestUpdate, setLatestUpdate] = useState(null)
  const [hasTurbineData, setHasTurbineData] = useState(false) // whether setup has turbine data
  const [showGeoLayer, setShowGeoLayer] = useState(true) // toggle state from map

  // Use Redux permissions if available, otherwise fall back to localStorage
  const userStoredPermissions = loadStateFromLocalStorage('permissions')
  const effectivePermissions =
    Array.isArray(userPermissions) && userPermissions.length > 0
      ? userPermissions
      : userStoredPermissions || []

  const isAdmin = effectivePermissions.includes('settings')

  const assignColorToParks = (data, isOverviewDAH) => {
    const capacities = data.map(d => d.InstalledCapacityKW)
    const minCapacity = Math.min(...capacities)
    const maxCapacity = Math.max(...capacities)

    const { minDahError, maxDahError } = getDahErrorRange(data)
    const enrichedData = data.map(d => {
      const color = isOverviewDAH
        ? getColorGreenWhiteRed(d.DahError, minDahError, maxDahError)
        : techColors[d.Technology] || techColors.Default

      const popupClass = d.Technology
        ? `popup-${d.Technology.toLowerCase()}`
        : 'popup-default'

      const radius = getRadius(d.InstalledCapacityKW, minCapacity, maxCapacity)

      const iconDiameter = Math.min(Math.max(2 * radius, 24), 60)
      const iconSize = [iconDiameter, iconDiameter]
      const iconAnchor = [iconDiameter / 2, iconDiameter / 2]

      const invisibleIcon = new L.Icon({
        iconUrl: 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=',
        iconSize,
        iconAnchor,
        popupAnchor: [0, 0],
      })

      return {
        ...d, // keep all original fields
        color,
        popupClass,
        radius,
        iconSize,
        iconAnchor,
        invisibleIcon,
      }
    })
    return enrichedData
  }

  // If there is only 1 available setup, select it automatically
  useEffect(() => {
    if (userSetups.length === 1 && selectedSetup !== userSetups[0]) {
      setSelectedUserSetup(userSetups[0])
    }
  }, [userSetups, selectedSetup, setSelectedUserSetup])

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        setPageError(false)
        setTableData(null)
        setMapData([])
        setSelectedPlants([])
        const response = await Api.Overview.getOverview(selectedSetup)

        if (response.status === 204) {
          setTableData(null)
          setNodataContent({
            heading: `No data for selected setup: ${selectedSetup}`,
            body: '',
          })
        }
        if (response.status === 200) {
          setTableData(response.data)
        }
        setLoading(false)
      } catch (error) {
        setTableData(null)
        setPageError(true)
        if (error.response.status === 411) {
          setNodataContent({
            heading: `Error running query for selected setup: ${selectedSetup}`,
            body: '',
          })
        } else {
          setNodataContent({
            heading: `Error fetching data for selected setup: ${selectedSetup}`,
            body: '',
          })
        }
        console.error('Error fetching portfolio overview data:', error)
      } finally {
        setLoading(false)
      }
    }

    if (selectedSetup !== null) {
      fetchData()
    } else {
      setPageError(false)
      setSelectedPlants([])
      setNodataContent({
        heading: 'No data',
        body: 'Select a setup to view portfolio overview',
      })
    }
  }, [selectedSetup])

  useEffect(() => {
    if (selectedSetup == null) {
      setIsOverviewDAH(null)
      return
    }

    setIsOverviewDAH(null)

    const fetchDahStatus = async () => {
      try {
        const response = await Api.Setups.getSetup(selectedSetup)
        if (response.status === 200) {
          setIsOverviewDAH(response.data?.setup?.is_overview_dah_error)
        } else {
          setIsOverviewDAH(false)
        }
      } catch (err) {
        console.error('Error fetching setups in overview data:', err)
        setIsOverviewDAH(false)
      }
    }

    fetchDahStatus()
  }, [selectedSetup])

  /** Initial turbine data fetch - only runs once per setup change */
  useEffect(() => {
    const fetchTurbines = async () => {
      try {
        setTurbineLoading(true)
        setTurbineDataRaw(null)
        setTurbineData([])
        setLatestUpdate(null)

        const response = await Api.Turbines.getTurbines(selectedSetup)
        if (response.status === 204) {
          setTurbineDataRaw(null)
          setHasTurbineData(false)
        }

        if (response.status === 200) {
          setTurbineDataRaw(response.data)
          setHasTurbineData(true) // Setup has turbine data
        }

        setTurbineLoading(false)
      } catch (error) {
        setTurbineDataRaw(null)
        setHasTurbineData(false) // No turbine data available

        // 404/204 = setup simply has no turbine data — not a real error
        const status = error?.response?.status
        if (status === 404 || status === 204) {
          // Expected for setups without turbine-level data, silently ignore
        } else {
          console.error('Error fetching turbine data:', error)
        }
      } finally {
        setTurbineLoading(false)
      }
    }

    if (selectedSetup !== null) {
      fetchTurbines()
    } else {
      setTurbineDataRaw(null)
      setHasTurbineData(false)
    }
  }, [selectedSetup])

  /** Polling effect: only poll when hasTurbineData=true AND showGeoLayer=true */
  useEffect(() => {
    if (!hasTurbineData || !showGeoLayer || selectedSetup === null) {
      return // Don't poll
    }

    // Set up 2-minute interval for polling
    const intervalId = setInterval(async () => {
      try {
        const response = await Api.Turbines.getTurbines(selectedSetup)
        if (response.status === 200) {
          setTurbineDataRaw(response.data)
        }
      } catch (error) {
        // Silently handle errors during polling
        const status = error?.response?.status
        if (status !== 404 && status !== 204) {
          console.error('Error polling turbine data:', error)
        }
      }
    }, 120000) // 2 minutes

    return () => clearInterval(intervalId)
  }, [hasTurbineData, showGeoLayer, selectedSetup])

  // Prepare the data for the map on update of
  // the table data, filtered plants, or selected plants
  useEffect(() => {
    if (isOverviewDAH === null) return
    let mappedData = []

    if (tableData && tableData.length > 0) {
      const updData = assignColorToParks(tableData, isOverviewDAH)

      // Determine which plants to show on map
      let plantsToShow
      if (selectedPlants && selectedPlants.length > 0) {
        // Show only checked plants (from filtered or all)
        plantsToShow = updData.filter(plant =>
          selectedPlants.includes(plant.Name),
        )
      } else if (filteredPlants === null) {
        // No filters active - show all plants
        plantsToShow = updData
      } else if (filteredPlants && filteredPlants.length > 0) {
        // Filters active with results - show filtered plants
        plantsToShow = updData.filter(plant =>
          filteredPlants.includes(plant.Name),
        )
      } else {
        // Filters active but no results - show nothing
        plantsToShow = []
      }

      mappedData = plantsToShow.map(plant => ({
        latitude: plant.Lat,
        longitude: plant.Lon,
        name: plant.Name,
        installedCapacity: parseFloat(
          (selectedUnit === 'kW'
            ? plant.InstalledCapacityKW
            : plant.InstalledCapacityKW / 1000
          ).toFixed(2),
        ),
        technology: plant.Technology,
        color: plant.color,
        popupClass: plant.popupClass,
        radius: plant.radius,
        iconSize: plant.iconSize,
        iconAnchor: plant.iconAnchor,
        invisibleIcon: plant.invisibleIcon,
        ...(isOverviewDAH && plant.DahError !== undefined
          ? {
              dahError:
                plant.DahError !== null
                  ? parseFloat(Number(plant.DahError).toFixed(2))
                  : null,
            }
          : {}),
      }))

      setMapData(mappedData)
    } else {
      setMapData([])
    }
  }, [tableData, selectedPlants, filteredPlants, selectedUnit, isOverviewDAH])

  /** Make sync selectedPlants with turbine data  */
  useEffect(() => {
    // If not loaded yet → reset filtered data
    if (!turbineDataRaw) {
      setTurbineData([])
      return
    }

    // Extract features
    const allFeatures = turbineDataRaw.features || []

    // If no selection → show all turbine areas
    if (!selectedPlants || selectedPlants.length === 0) {
      setLatestUpdate(turbineDataRaw?.latest_update)
      setTurbineData(allFeatures)
      return
    }

    // If selectedPlants exists, filter the features by their property "name"
    const filtered = allFeatures.filter(feat =>
      selectedPlants.includes(feat.properties.name),
    )

    setTurbineData(filtered)
  }, [turbineDataRaw, selectedPlants])

  // Wait for all parallel fetches to finish before rendering the map
  const allDataReady = !loading && !turbineLoading && isOverviewDAH !== null

  const resultPanel = () => {
    if (
      tableData === null ||
      tableData.length === 0 ||
      selectedSetup === null
    ) {
      return (
        <div className={classes.centerItem}>
          <div className={classes.alignNoData}>
            <NoSite
              {...props}
              creating={false}
              heading={nodataContent.heading}
              body={nodataContent.body}
            ></NoSite>
          </div>
        </div>
      )
    }

    // Calculate total capacity
    let totalCapacity = 0
    tableData.forEach(plant => {
      totalCapacity += plant['InstalledCapacityKW']
    })

    let selectedCapacity = 0
    selectedPlants.forEach(plant => {
      // Find the plant in the table data
      const plantData = tableData.find(p => p.Name === plant)
      selectedCapacity += plantData['InstalledCapacityKW']
    })

    if (!allDataReady) {
      return (
        <div className={classes.centerItem}>
          <CircularProgress />
        </div>
      )
    }

    return (
      <Grid
        container
        spacing={2}
        className={classes.mainGrid}
        alignItems="stretch" // so both columns fill full height
      >
        {/* ---------------------- MAP ------------------------ */}
        <Grid item xs={9} className={classes.mapGrid}>
          <GlobalLeafletMap
            mapKey={selectedSetup}
            data={mapData}
            turbineData={turbineData}
            latestUpdate={latestUpdate}
            unit={selectedUnit}
            isOverviewDAH={isOverviewDAH}
            showGeoLayer={showGeoLayer}
            onToggleGeoLayer={setShowGeoLayer}
          />
        </Grid>

        {/* ------------------- INFOBOXES --------------------- */}
        <Grid item xs={3} className={classes.dataGrid}>
          <Grid
            container
            direction="column"
            spacing={2}
            style={{ height: '100%' }}
            justifyContent="flex-start"
          >
            <Grid item>
              <InfoBox title="Number of Plants" content={tableData.length} />
            </Grid>
            <Grid item>
              <InfoBox
                title={`Total Capacity (${selectedUnit})`}
                content={
                  selectedUnit === 'kW' ? totalCapacity : totalCapacity / 1000
                }
              />
            </Grid>
            <Grid item>
              <InfoBox
                title="Selected Plants"
                content={selectedPlants.length}
              />
            </Grid>
            <Grid item>
              <InfoBox
                title={`Selected Capacity (${selectedUnit})`}
                content={
                  selectedUnit === 'kW'
                    ? selectedCapacity
                    : selectedCapacity / 1000
                }
              />
            </Grid>
          </Grid>
        </Grid>

        {/* --------------------- TABLE ----------------------- */}
        <Grid item xs={12} className={classes.tableGrid}>
          <PortfolioOverviewTable
            selectedSetup={selectedSetup}
            data={tableData}
            selectedPlants={selectedPlants}
            setSelectedPlants={setSelectedPlants}
            onFilteredPlantsChange={setFilteredPlants}
            unit={selectedUnit}
            isOverviewDAH={isOverviewDAH}
          />
        </Grid>
      </Grid>
    )
  }

  // With a single setup there is nothing to select. Admins still need the
  // drawer for the report controls, everyone else gets the full width.
  if (userSetups.length === 1 && !isAdmin) {
    if (loading) {
      return (
        <div className={classes.centerItem}>
          <CircularProgress />
        </div>
      )
    }
    return resultPanel()
  }

  return (
    <PortfolioOverviewPanel
      container={container}
      userSetups={userSetups}
      setSetup={setSelectedUserSetup}
      selectedSetup={selectedSetup}
      loading={loading}
      pageError={pageError}
      isAdmin={isAdmin}
    >
      {!loading ? (
        resultPanel()
      ) : (
        <div className={classes.centerItem}>
          <CircularProgress />
        </div>
      )}
    </PortfolioOverviewPanel>
  )
}

const mapStateToProps = state => ({
  selectedSetup: state.userSetups.selectedSetup,
  userSetups: state.userSetups.userSetups,
  userPermissions: state.userPermissions.permissions,
})

const mapDispatchToProps = {
  setSelectedUserSetup,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(PortfolioOverviewPage)
